import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CommandIcon from './CommandIcon.jsx';
import { formatRecordingTime, MAX_VOICE_BYTES, MAX_VOICE_SECONDS, recordingMimeType, sendVoiceRecording, voiceErrorKey } from '../lib/voice.js';

export default function VoiceRecorder({ credentials, ended, onOpenChange, t }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [errorKey, setErrorKey] = useState('');
  const [senderName, setSenderName] = useState(() => { try { return localStorage.getItem('cynoops.live.voiceName') || ''; } catch { return ''; } });
  const launchRef = useRef(null), dialogRef = useRef(null), streamRef = useRef(null), recorderRef = useRef(null);
  const timerRef = useRef(null), startedRef = useRef(0), stoppedRef = useRef(0), generationRef = useRef(0);
  const messageIdRef = useRef(''), durationRef = useRef(0), sendControllerRef = useRef(null), mountedRef = useRef(true);
  const sending = phase === 'sending';

  const releaseMicrophone = useCallback(() => {
    clearInterval(timerRef.current); timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop()); streamRef.current = null;
  }, []);
  const clearCapture = useCallback(() => {
    generationRef.current += 1;
    const recorder = recorderRef.current; recorderRef.current = null;
    if (recorder) {
      recorder.ondataavailable = null; recorder.onstop = null; recorder.onerror = null;
      try { if (recorder.state !== 'inactive') recorder.stop(); } catch {}
    }
    releaseMicrophone();
  }, [releaseMicrophone]);
  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    stoppedRef.current = performance.now();
    setPhase('stopping'); recorder.stop(); releaseMicrophone();
  }, [releaseMicrophone]);
  const startRecording = useCallback(async () => {
    clearCapture(); setBlob(null); setElapsed(0); setErrorKey('');
    if (ended) { setErrorKey('voice.sessionEnded'); setPhase('idle'); return; }
    if (!navigator.mediaDevices?.getUserMedia || !globalThis.MediaRecorder) { setErrorKey('voice.unsupported'); setPhase('idle'); return; }
    const generation = generationRef.current;
    setPhase('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mountedRef.current || generationRef.current !== generation) { stream.getTracks().forEach((track) => track.stop()); return; }
      streamRef.current = stream;
      const mimeType = recordingMimeType();
      const recorder = new MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 64000 });
      recorderRef.current = recorder;
      const chunks = []; let totalBytes = 0, tooLarge = false;
      recorder.ondataavailable = (event) => {
        if (generationRef.current !== generation || !event.data?.size) return;
        totalBytes += event.data.size;
        if (totalBytes > MAX_VOICE_BYTES) { tooLarge = true; stopRecording(); return; }
        chunks.push(event.data);
      };
      recorder.onstop = () => {
        releaseMicrophone(); recorderRef.current = null;
        if (!mountedRef.current || generationRef.current !== generation) return;
        const duration = Math.min(MAX_VOICE_SECONDS, ((stoppedRef.current || performance.now()) - startedRef.current) / 1000);
        if (tooLarge || !chunks.length || duration < 0.1) { setErrorKey(tooLarge ? 'voice.tooLarge' : 'voice.empty'); setPhase('idle'); return; }
        durationRef.current = duration; setElapsed(duration);
        messageIdRef.current = crypto.randomUUID();
        setBlob(new Blob(chunks, { type: recorder.mimeType || mimeType || chunks[0].type })); setPhase('review');
      };
      recorder.onerror = () => {
        if (generationRef.current !== generation) return;
        clearCapture(); setErrorKey('voice.microphoneUnavailable'); setPhase('idle');
      };
      startedRef.current = performance.now(); stoppedRef.current = 0;
      recorder.start(1000); setPhase('recording');
      timerRef.current = setInterval(() => {
        const seconds = (performance.now() - startedRef.current) / 1000;
        setElapsed(Math.min(seconds, MAX_VOICE_SECONDS));
        if (seconds >= MAX_VOICE_SECONDS) stopRecording();
      }, 250);
    } catch (error) {
      if (!mountedRef.current || generationRef.current !== generation) return;
      clearCapture(); setErrorKey(voiceErrorKey(error)); setPhase('idle');
    }
  }, [clearCapture, ended, releaseMicrophone, stopRecording]);

  const close = useCallback(() => {
    if (sending) return;
    clearCapture(); setBlob(null); setOpen(false); onOpenChange(false); setPhase('idle'); setErrorKey('');
    requestAnimationFrame(() => launchRef.current?.focus());
  }, [clearCapture, onOpenChange, sending]);
  const send = async () => {
    if (!blob || sending || ended) return;
    setErrorKey(''); setPhase('sending');
    const controller = new AbortController(); sendControllerRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), 90000);
    try {
      await sendVoiceRecording({ credentials, messageId: messageIdRef.current, blob, durationSeconds: durationRef.current, senderName: senderName.trim(), signal: controller.signal });
      if (!mountedRef.current) return;
      try { localStorage.setItem('cynoops.live.voiceName', senderName.trim()); } catch {}
      setPhase('sent'); setBlob(null);
    } catch (error) { if (mountedRef.current) { setErrorKey(voiceErrorKey(error)); setPhase('review'); } }
    finally { clearTimeout(timeout); sendControllerRef.current = null; }
  };
  useEffect(() => {
    if (!blob) { setPreviewUrl(''); return undefined; }
    const url = URL.createObjectURL(blob); setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; clearCapture(); sendControllerRef.current?.abort(); };
  }, [clearCapture]);
  useEffect(() => {
    const stopWhenHidden = () => { if (document.visibilityState === 'hidden') stopRecording(); };
    document.addEventListener('visibilitychange', stopWhenHidden);
    return () => document.removeEventListener('visibilitychange', stopWhenHidden);
  }, [stopRecording]);
  useEffect(() => { if (ended) stopRecording(); }, [ended, stopRecording]);
  useEffect(() => {
    if (!open) return undefined;
    dialogRef.current?.querySelector('button:not(:disabled)')?.focus();
    const handleKey = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
      if (event.key !== 'Tab') return;
      const items = [...(dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), audio[controls]') || [])];
      if (!items.length) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === items[0]) { event.preventDefault(); items.at(-1).focus(); }
      else if (!event.shiftKey && document.activeElement === items.at(-1)) { event.preventDefault(); items[0].focus(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [close, open]);

  return <>
    <button ref={launchRef} type="button" className="live-record-launcher" data-testid="live-record-button" disabled={ended}
      onClick={() => { setOpen(true); onOpenChange(true); void startRecording(); }} aria-label={t('voice.record')} title={ended ? t('voice.sessionEnded') : t('voice.record')}>
      <CommandIcon name="microphone" /><span>{t('voice.record')}</span>
    </button>
    {open && createPortal(<div className="voice-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="voice-title" className="voice-dialog" data-testid="voice-dialog">
        <header className="settings-dialog__header"><CommandIcon name="microphone" /><h2 id="voice-title">{t('voice.title')}</h2>
          <button type="button" className="icon-button" disabled={sending} onClick={close} aria-label={t('settings.close')}><CommandIcon name="x" /></button>
        </header>
        <div className="voice-dialog__body">
          {phase === 'requesting' && <p role="status"><span className="spinner" />{t('voice.requesting')}</p>}
          {(phase === 'recording' || phase === 'stopping') && <div className="voice-recording-state" role="status">
            <span className="voice-recording-pulse"><CommandIcon name="microphone" /></span>
            <strong data-testid="recording-timer">{formatRecordingTime(elapsed)}</strong><span>{t('voice.limit')}</span>
            <button type="button" className="button voice-stop-button" onClick={stopRecording} disabled={phase === 'stopping'} data-testid="voice-stop"><CommandIcon name="stop" />{t('voice.stop')}</button>
          </div>}
          {(phase === 'review' || sending) && <div className="voice-review">
            <p>{t('voice.preview')}</p><audio controls src={previewUrl || undefined} preload="metadata" data-testid="voice-preview" />
            <label htmlFor="voice-sender">{t('voice.name')}</label><input id="voice-sender" value={senderName} maxLength={80} disabled={sending} onChange={(event) => setSenderName(event.target.value)} autoComplete="name" />
          </div>}
          {phase === 'sent' && <div className="voice-sent" role="status"><CommandIcon name="check-circle" /><h3>{t('voice.sent')}</h3><p>{t('voice.sentBody')}</p></div>}
          {errorKey && <p className="inline-error" role="alert">{t(errorKey)}</p>}
          {ended && phase !== 'sent' && <p className="inline-error" role="alert">{t('voice.sessionEnded')}</p>}
          {phase === 'idle' && <button type="button" className="button button--primary" disabled={ended} onClick={() => void startRecording()}>{t('voice.retry')}</button>}
        </div>
        <footer className="settings-dialog__footer">
          {phase === 'sent' ? <button type="button" className="button button--primary" onClick={close}>{t('settings.done')}</button> : <>
            <button type="button" className="button" onClick={close} disabled={sending}>{t('voice.discard')}</button>
            {phase === 'review' && <button type="button" className="button" disabled={ended} onClick={() => void startRecording()}>{t('voice.again')}</button>}
            {(phase === 'review' || sending) && <button type="button" className="button button--primary" disabled={sending || ended} onClick={() => void send()} data-testid="voice-send">
              {sending ? <span className="spinner" /> : <CommandIcon name="paper-plane-tilt" />}{t(sending ? 'voice.sending' : 'voice.send')}
            </button>}
          </>}
        </footer>
      </section>
    </div>, document.body)}
  </>;
}
